import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api';

export const currentDatasets = createAsyncThunk('admin/currentDatasets', async () => {
    const response = await axiosInstance.get(`/rest/admin/current/datasets`);
    return response.data;
});

export const availableDatasets = createAsyncThunk('admin/availableDatasets', async () => {
    const response = await axiosInstance.get(`/rest/admin/available/datasets`);
    return response.data;
});

export const refreshDatasetLogs = createAsyncThunk('admin/refreshLogs', async () => {
    const response = await axiosInstance.get(`/rest/admin/refresh/logs`);
    return response.data;
});

export const dataloadLogs = createAsyncThunk('admin/dataloadLogs', async () => {
    const response = await axiosInstance.get(`/rest/admin/dataload/logs`);
    return response.data;
});

export const apiVersion = createAsyncThunk('admin/version', async () => {
    const response = await axiosInstance.get(`/rest/api/version`);
    return response.data;
});

export const dataloadProcessLogs = createAsyncThunk('admin/dataloadProcessLogs', async (exeId: string) => {
    const response = await axiosInstance.get(`/rest/admin/dataload/process/logs/${exeId}`);
    return response.data;
});

export const refreshDataset = createAsyncThunk('admin/refreshDataset', async (dataType: string) => {
    const response = await axiosInstance.post(`/rest/admin/broadcast/datatype/refresh/${dataType}`);
    return response.data;
});

export const clearCache = createAsyncThunk('admin/clearCache', async (dataType: string) => {
    const response = await axiosInstance.post(`/rest/admin/broadcast/cache/clear/${dataType}`);
    return response.data;
});

export const reloadSchema = createAsyncThunk('admin/reloadSchema', async (dataType: string) => {
    const response = await axiosInstance.post(`/rest/admin/broadcast/datatype/refresh/${dataType}?reloadOnly=true`);
    return response.data;
});

export const triggerLoad = createAsyncThunk('admin/triggerLoad', async (dataType: string) => {
    const response = await axiosInstance.post(`/rest/admin/trigger/load/${dataType}`);
    return response.data;
});

export const uploadTrainingVideo = createAsyncThunk<string, FormData>('admin/uploadTrainingVideo', async (formData) => {
    const response = await axiosInstance.post(`/rest/admin/uploadTrainingVideo`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
});

export type TrainingVideoResponse = {
    trainingVideoId: number;
    title: string;
    description: string;
    fileKey: string;
    fileExtension: string;
    category: string;
    uploadedDt: number;
    modifiedDt: number;
};

export const fetchTrainingVideos = createAsyncThunk<TrainingVideoResponse[]>('admin/fetchTrainingVideos', async () => {
    const response = await axiosInstance.get(`/rest/admin/trainingVideos`);
    return response.data;
});

export const downloadTrainingVideo = createAsyncThunk<Blob, number>('admin/downloadTrainingVideo', async (trainingVideoId) => {
    const response = await axiosInstance.get(`/rest/admin/trainingVideo/${trainingVideoId}`, {
        responseType: 'blob'
    });
    return response.data;
});

export const deleteTrainingVideo = createAsyncThunk<void, number>('admin/deleteTrainingVideo', async (id) => {
    await axiosInstance.delete(`/rest/admin/trainingVideos/${id}`);
});

type UpdateTrainingVideoRequest = {
    id: number;
    formData: FormData;
};

export const updateTrainingVideo = createAsyncThunk<unknown, UpdateTrainingVideoRequest>('admin/updateTrainingVideo', async ({ id, formData }) => {
    const response = await axiosInstance.post(`/rest/admin/trainingVideos/${id}/update`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
});



interface Dataset {
    dataType: string;
    reportDate: string;
}



interface AdminState {
    currentDatasets: Dataset[];
    availableDatasets: Dataset[];
    loading: boolean;
    status: string;
    confirmPopUp: any;
    showConfirmPopup: boolean;
    refreshDatasetLogs: any[];
    dataloadLogs: any[];
    dataloadProcessLogs: any[];
}

// Initial state
const initialState: AdminState = {
    currentDatasets: [],
    availableDatasets: [],
    loading: false,
    status: '',
    confirmPopUp: {
        title: "",
        message: "",
        confirmAction: (title: string, id: number) => { },
        closeAction: () => { },
        id: 0,
    },
    showConfirmPopup: false,
    refreshDatasetLogs: [],
    dataloadLogs: [],
    dataloadProcessLogs: []
}



const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        setConfirmPopUp: (state, action) => {
            state.confirmPopUp = action.payload;
            state.showConfirmPopup = true;
        },
        hideConfirmPopup: (state, action) => {
            state.showConfirmPopup = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(currentDatasets.fulfilled, (state, action) => {
                state.currentDatasets = action.payload;
            })
            .addCase(availableDatasets.fulfilled, (state, action) => {
                state.availableDatasets = action.payload;
            }).addCase(refreshDataset.fulfilled, (state, action) => {
                state.loading = false;
                state.status = 'success';
            }).addCase(refreshDataset.rejected, (state, action) => {
                state.loading = false;
                state.status = 'error';
            }).addCase(refreshDataset.pending, (state, action) => {
                state.loading = true;
                state.status = '';
                state.showConfirmPopup = false;
            }).addCase(clearCache.fulfilled, (state, action) => {
                state.loading = false;
                state.status = 'success';
            }).addCase(clearCache.rejected, (state, action) => {
                state.loading = false;
                state.status = 'error';
            }).addCase(clearCache.pending, (state, action) => {
                state.loading = true;
                state.status = '';
                state.showConfirmPopup = false;
            }).addCase(reloadSchema.fulfilled, (state, action) => {
                state.loading = false;
                state.status = 'success';
            }).addCase(reloadSchema.rejected, (state, action) => {
                state.loading = false;
                state.status = 'error';
            }).addCase(reloadSchema.pending, (state, action) => {
                state.loading = true;
                state.status = '';
                state.showConfirmPopup = false;
            }).addCase(triggerLoad.fulfilled, (state, action) => {
                state.loading = false;
                state.status = 'success';
            }).addCase(triggerLoad.rejected, (state, action) => {
                state.loading = false;
                state.status = 'error';
            }).addCase(triggerLoad.pending, (state, action) => {
                state.loading = true;
                state.status = '';
                state.showConfirmPopup = false;
            }).addCase(refreshDatasetLogs.fulfilled, (state, action) => {
                state.refreshDatasetLogs = action.payload;
            }).addCase(dataloadLogs.fulfilled, (state, action) => {
                state.dataloadLogs = action.payload;
            }).addCase(dataloadProcessLogs.fulfilled, (state, action) => {
                state.dataloadProcessLogs = action.payload;
            });

    },
});

export const { setConfirmPopUp, hideConfirmPopup} = adminSlice.actions;
export default adminSlice.reducer;
